import React from 'react';
import { Box, Typography } from '@mui/material';
import { OUTCOME, STATUS_TONE } from '../../../services/sprintHealthService';
import { A, APill, ABtn, Panel, Cap, Bars, RagChip } from './reviewUi';
import {
  recent, average, attentionItems,
  goalRag, commitRag, effortRag, reliabilityRag, velocityRag, cycleRag, bugsRag, automatedRag, practiceRag, retroRag,
} from './rag';
import { qualityOf } from './ReviewDetails';

const outcomeLabel = (o) => OUTCOME[o]?.label.replace(/^Goal /, '').replace(/^./, (m) => m.toUpperCase());

/** Every status rating for the sprint, keyed by tile id. */
export const ratings = (c, hist, delivery) => {
  const q = qualityOf(c);
  return {
    goal: goalRag(c), commit: commitRag(delivery), effort: effortRag(c), reliability: reliabilityRag(c), velocity: velocityRag(c, hist),
    cycle: cycleRag(c), bugs: bugsRag(q), tests: { tone: 'n', reason: '' }, automated: automatedRag(q), practice: practiceRag(c), retro: retroRag(c),
  };
};

const bigNum = { fontSize: 26, fontWeight: 300, letterSpacing: '-.02em', lineHeight: 1.25, color: A.ink, '& small': { fontSize: 13, color: A.ink3, ml: 0.5, letterSpacing: 0 } };

const bar = (parts) => (
  <Box role="img" sx={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', bgcolor: A.line, mt: 1 }}>
    {parts.map(([w, color], k) => w > 0 && <Box key={k} sx={{ width: `${w}%`, bgcolor: color }} />)}
  </Box>
);

const Tile = ({ label, value, unit, sub, rag, viz, onClick, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', height: '100%',
      textAlign: 'left', font: 'inherit', cursor: 'pointer', bgcolor: A.surface, border: `1px solid ${A.line}`, borderRadius: '12px', px: 2, py: 1.5, minWidth: 0,
      transition: 'transform .2s cubic-bezier(.4,0,.2,1), box-shadow .2s cubic-bezier(.4,0,.2,1)',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 10px rgba(0,0,0,.08), 0 3px 6px rgba(0,0,0,.06)' },
      '&:focus-visible': { outline: `2px solid ${A.btn}`, outlineOffset: 2 },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
    }}
  >
    <Typography component="div" sx={{ color: A.ink2, fontSize: 13, fontWeight: 500 }}>{label}</Typography>
    {children || (
      <>
      <Typography component="div" sx={{ fontSize: 26, fontWeight: 300, letterSpacing: '-.02em', lineHeight: 1.25, color: A.ink, '& small': { fontSize: 13, color: A.ink3, ml: 0.5, letterSpacing: 0 } }}>
        {value}{unit && value !== '—' && <small>{unit}</small>}
      </Typography>
      {sub && <Cap>{sub}</Cap>}
      {viz}
      {rag && rag.tone !== 'n' && <Box sx={{ mt: 1 }}><RagChip rag={rag} /></Box>}
      </>
    )}
  </Box>
);

const THREE = { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' };

const TileGroup = ({ title, children, cols = THREE }) => (
  <Box sx={{ mb: 2 }}>
    <Typography component="h2" sx={{ fontSize: 13, fontWeight: 600, color: A.sageInk, mb: 0.75 }}>{title}</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 1.5 }}>{children}</Box>
  </Box>
);

/** All the sprint's numbers as clickable tiles. Click one to open its section on the Details tab. */
export const SummaryTiles = ({ c, hist, delivery, onSelect }) => {
  const r = ratings(c, hist, delivery);
  const a = c.allocation;
  const unit = a.basis === 'sp' ? 'SP' : 'items';
  const q = qualityOf(c);
  const h5 = recent(hist, 5);
  const avgVel = Math.round(average(hist.map((m) => m.velocity)) || 0);
  const goalDots = h5.map((m) => ({ met: A.good, partly: '#d99a3a', not_met: A.bad }[m.goal.outcome] || A.line));
  const labels = h5.map((m) => m.shortName);
  const goalItems = c.goal.text ? 'from the ADO sprint goal' : 'No goal in ADO yet';
  const t = (id, props) => <Tile key={id} {...props} rag={r[id]} onClick={() => onSelect(id)} />;
  return (
    <Box>
      <TileGroup title="Delivery">
        {t('goal', {
          label: 'Sprint goal', value: outcomeLabel(c.goal.outcome) || 'Not set', sub: c.goal.manual ? 'Set by the team' : goalItems,
          viz: (
            <>
              <Box sx={{ display: 'flex', gap: '4px', mt: 1 }}>{goalDots.map((bg, k) => <Box key={k} title={labels[k]} sx={{ flex: 1, height: 8, borderRadius: '2px', bgcolor: bg }} />)}</Box>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: 10.5, color: A.ink3 }}>{labels.map((l) => <Box key={l} sx={{ flex: 1, textAlign: 'center' }}>{l}</Box>)}</Box>
            </>
          ),
        })}
        {t('commit', {
          label: 'Commitments moved', value: delivery.moved, unit: `of ${delivery.total}`, sub: `${c.quarter} ${c.year} commitments`,
          viz: delivery.total ? bar([[(delivery.moved / delivery.total) * 100, A.sageInk]]) : null,
        })}
        {t('effort', {
          label: 'Delivered', value: a.total, unit, sub: `${c.shares.onCommitments}% on ${c.quarter} plan, ${c.unplannedPct}% unplanned`,
          viz: a.total ? <>{bar([[(a.goal / a.total) * 100, A.sageInk], [(a.quarterly / a.total) * 100, A.sage], [(a.bau / a.total) * 100, A.bau], [(a.unlinked / a.total) * 100, A.warn]])}<Cap sx={{ mt: 0.5 }}>{a.unlinked} {unit} not linked</Cap></> : null,
        })}
      </TileGroup>

      <TileGroup title="Flow">
        {t('reliability', {
          label: 'Forecast reliability', value: c.forecast.reliability ?? '—', unit: '%', sub: `${c.forecast.doneOfCommitted} of ${c.forecast.committed} planned items done`,
          viz: <Bars height={30} max={100} suffix="%" items={h5.map((m) => ({ label: m.shortName, value: m.forecast.reliability, current: m.sprintName === c.sprintName }))} />,
        })}
        {t('velocity', {
          label: 'Velocity', value: c.velocity, unit: 'SP', sub: `avg ${avgVel} SP`,
          viz: <Bars height={30} suffix=" SP" items={h5.map((m) => ({ label: m.shortName, value: m.velocity, current: m.sprintName === c.sprintName }))} />,
        })}
        {t('cycle', { label: 'Cycle time', value: c.cycle.median != null ? c.cycle.median : '—', unit: 'days', sub: c.cycle.p85 != null ? `median · 85th percentile ${c.cycle.p85} days` : 'median' })}
      </TileGroup>

      <TileGroup title="Quality">
        {t('bugs', { label: 'Bugs raised', value: q.bugsRaised, unit: 'bugs', sub: `${q.bugsProduction} prod, ${q.bugsNonProduction} non-prod` })}
        <Tile label="Tests" onClick={() => onSelect('tests')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.25 }}>
            <Box>
              <Typography component="div" sx={bigNum}>{q.testsCreated}<small>created</small></Typography>
              <Cap>created or linked this sprint</Cap>
              {r.tests && r.tests.tone !== 'n' && <Box sx={{ mt: 1 }}><RagChip rag={r.tests} /></Box>}
            </Box>
            <Box sx={{ borderLeft: `1px solid ${A.line}`, pl: 2 }}>
              <Typography component="div" sx={bigNum}>{q.testsAutomated}<small>automated</small></Typography>
              <Cap>of {q.testsCreated} tests, {q.testsManual} manual</Cap>
              {r.automated && r.automated.tone !== 'n' && <Box sx={{ mt: 1 }}><RagChip rag={r.automated} /></Box>}
            </Box>
          </Box>
        </Tile>
        <Tile label="Work items" value={(c.stats?.pbis || 0) + (c.stats?.bugs || 0)} sub={`${c.stats?.pbis || 0} PBIs, ${c.stats?.bugs || 0} bugs · ${c.stats?.storyPoints || 0} SP`} onClick={() => onSelect('items')} />
      </TileGroup>


    </Box>
  );
};


/** Verdict and what needs attention. */
export const SummaryHeader = ({ data, delivery, editable, onSelect, onSync }) => {
  const c = data.current;
  const attn = attentionItems(c);
  const shown = attn.filter((x) => x.key !== 'retro');
  const unit = c.allocation.basis === 'sp' ? 'SP' : 'items';
  const counts = Object.values(ratings(c, data.history, delivery)).reduce((m, x) => ({ ...m, [x.tone]: (m[x.tone] || 0) + 1 }), {});
  const act = (x) => {
    if (x.action === 'sync') onSync();
    else onSelect(x.action, x.action === 'effort');
  };
  const flagText = data.flags.length ? `Flagged to leadership: ${data.flags.map((f) => f.text).join('; ')}.` : 'Nothing flagged to leadership. Last 3 sprints are within healthy ranges.';
  return (
    <Panel sx={{ p: { xs: 2, md: 3 }, height: '100%', boxSizing: 'border-box' }}>
      <APill tone={STATUS_TONE[data.status.code]}>{data.status.label}</APill>
      <Typography sx={{ fontSize: 19, fontWeight: 300, mt: 1, maxWidth: '70ch', lineHeight: 1.4, color: A.ink }}>
        {c.allocation.total ? `${c.allocation.total} ${unit} delivered, ${c.shares.onCommitments}% on the ${c.quarter} plan. ` : 'Nothing completed in this sprint yet. '}
        {attn.length ? `${attn.length} thing${attn.length === 1 ? ' needs' : 's need'} attention before this sprint can be called healthy.` : 'Nothing needs attention.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {[['r', 'Red'], ['a', 'Amber'], ['g', 'Green']].map(([k, label]) => counts[k] > 0 && <RagChip key={k} rag={{ tone: k, reason: '' }} text={`${counts[k]} ${label}`} />)}
      </Box>
      <Cap sx={{ mt: 1 }}>{flagText}</Cap>

      {shown.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 2, p: 0 }}>
          {shown.map((x) => (
            <Box component="li" key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1.5, borderTop: `1px solid ${A.line}` }}>
              <Box><Typography sx={{ fontWeight: 500, color: A.ink }}>{x.title}</Typography><Cap>{x.detail}</Cap></Box>
              {(x.action !== 'sync' || editable) && <ABtn sx={{ height: 32 }} onClick={() => act(x)}>{x.label}</ABtn>}
            </Box>
          ))}
        </Box>
      )}

    </Panel>
  );
};
